import { useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import VideoCard from "@/components/base/VideoCard";
import VideoBrowser from "@/components/feature/VideoBrowser";
import {
  allVideos,
  getHotVideos,
  getVideoTypes,
  searchVideos,
} from "@/data/videos";
import usePageMeta from "@/hooks/usePageMeta";

export default function SearchPage() {
  const [params, setParams] = useSearchParams();
  const keyword = params.get("q") ?? "";
  const [input, setInput] = useState(keyword);

  useEffect(() => {
    setInput(keyword);
  }, [keyword]);

  const results = useMemo(
    () => (keyword.trim() ? searchVideos(keyword) : allVideos),
    [keyword],
  );
  const typeOptions = useMemo(() => getVideoTypes(), []);
  const recommendations = useMemo(() => getHotVideos(4), []);

  const hasKeyword = keyword.trim().length > 0;
  const noResult = hasKeyword && results.length === 0;

  usePageMeta(
    hasKeyword
      ? `搜索“${keyword}”结果 - 免费视频网站91`
      : "视频搜索 - 免费视频网站91",
    hasKeyword
      ? `免费视频网站91 为你找到与“${keyword}”相关的免费高清视频内容，支持分类筛选与多种排序。`
      : "免费视频网站91 搜索页面，输入关键词即可查找电影、电视剧、综艺、动画等免费高清视频。",
    `免费视频网站91,${hasKeyword ? keyword : "视频搜索"},免费视频,在线视频观看,免费高清视频`,
  );

  const submit = (e: React.FormEvent) => {
    e.preventDefault();
    const kw = input.trim();
    setParams(kw ? { q: kw } : {});
  };

  return (
    <>
      <div className="border-b border-background-200/70 bg-background-100">
        <div className="container-page py-8 md:py-10">
          <h1 className="font-heading text-2xl font-extrabold text-foreground-950 md:text-3xl">
            {hasKeyword ? (
              <>
                搜索“<span className="text-primary-600">{keyword}</span>”的结果
              </>
            ) : (
              "浏览全部视频"
            )}
          </h1>
          <p className="mt-2 text-sm text-foreground-500">
            输入关键词，快速找到你想要的免费高清视频资源
          </p>

          <form onSubmit={submit} className="mt-5 flex w-full max-w-xl flex-col gap-2 sm:flex-row">
            <div className="relative flex-1">
              <i className="ri-search-line pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-base text-foreground-400"></i>
              <input
                type="text"
                value={input}
                onChange={(e) => setInput(e.target.value)}
                placeholder="搜索视频标题、分类、标签"
                aria-label="搜索关键词"
                className="w-full rounded-md border border-background-200 bg-background-50 py-2.5 pl-10 pr-3 text-sm text-foreground-900 outline-none placeholder:text-foreground-400 focus:border-primary-400"
              />
            </div>
            <button
              type="submit"
              className="inline-flex cursor-pointer items-center justify-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-6 py-2.5 text-sm font-semibold text-background-50 transition-colors hover:bg-primary-600"
            >
              <i className="ri-search-line"></i>
              搜索
            </button>
          </form>
        </div>
      </div>

      <div className="container-page py-8 md:py-10">
        {noResult ? (
          <div className="flex flex-col items-center rounded-lg border border-dashed border-background-300 bg-background-100 py-14 text-center">
            <span className="flex h-16 w-16 items-center justify-center rounded-full bg-background-200 text-foreground-400">
              <i className="ri-search-eye-line text-3xl"></i>
            </span>
            <h2 className="mt-4 font-heading text-lg font-bold text-foreground-900">
              没有找到与“{keyword}”相关的视频
            </h2>
            <p className="mt-1 max-w-md text-sm text-foreground-500">
              换个关键词试试，或者看看下面这些热门推荐，说不定正合你意。
            </p>
            <Link
              to="/"
              className="mt-5 inline-flex cursor-pointer items-center gap-2 whitespace-nowrap rounded-md border border-background-300 bg-background-50 px-5 py-2.5 text-sm font-semibold text-foreground-800 transition-colors hover:border-primary-400 hover:text-primary-600"
            >
              <i className="ri-home-4-line"></i>
              返回首页
            </Link>

            <div className="mt-12 w-full text-left">
              <h3 className="mb-4 flex items-center gap-2 font-heading text-lg font-bold text-foreground-950">
                <i className="ri-fire-line text-accent-500"></i>
                热门推荐
              </h3>
              <div className="grid grid-cols-2 gap-3 md:grid-cols-4 md:gap-5">
                {recommendations.map((video) => (
                  <VideoCard key={video.id} video={video} showCategory />
                ))}
              </div>
            </div>
          </div>
        ) : (
          <>
            {hasKeyword && (
              <p className="mb-4 text-sm text-foreground-500">
                关键词“<span className="font-semibold text-foreground-900">{keyword}</span>”
                共匹配到 <span className="font-semibold text-primary-600">{results.length}</span> 个视频
              </p>
            )}
            <VideoBrowser videos={results} typeOptions={typeOptions} pageSize={8} />
          </>
        )}
      </div>
    </>
  );
}